export type LogType = "feed" | "diaper" | "sleep" | "weight" | "marker" | "vomiting" | "hiccup" | "fever" | "pain" | "pump" | "file" | "note" | "vaccine" | "task_completed";
export type Status = "normal" | "attention" | "alert";

// Kept here (rather than in FormOverlay, where it's also used) so App.tsx can reference it
// without eagerly importing the whole lazy-loaded FormOverlay module.
export const WEBMCP_LOG_TYPES = ["feed", "sleep", "diaper", "weight", "pump", "note"] as const;

export interface BabyProfile {
  id: string;
  name: string;
  birthDate: string;
  ownerUid: string;
  collaboratorEmails: string[];
  feedingMode?: "bottle" | "nursing" | "both";
  /** @deprecated use UserSettings.unitSystem instead */
  preferredUnit?: "ml" | "oz";
  sex?: "boy" | "girl";
  countryCode?: "US" | "UK" | "DE" | "TR" | "Generic";
  /** First-touch campaign attribution (UTM params / gclid) captured when the visitor first landed, if any. */
  acquisition?: {
    source?: string;
    medium?: string;
    campaign?: string;
    term?: string;
    content?: string;
    gclid?: string;
  };
}

export type UnitSystem = "metric" | "imperial";

export const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English" },
  { code: "de", label: "Deutsch" },
  { code: "es", label: "Español" },
  { code: "tr", label: "Türkçe" },
  { code: "hi", label: "हिन्दी" },
  { code: "ar", label: "العربية" },
  { code: "it", label: "Italiano" },
  { code: "ru", label: "Русский" },
] as const;

export interface UserSettings {
  unitSystem?: UnitSystem;
  language?: string;
  aiEnabled?: boolean;
  sleepDayCutoff?: string;
}

export interface LogPayload {
  amountMl?: number;
  feedMethod?: "bottle" | "nursing";
  nursingSide?: "left" | "right" | "both";
  diaperKind?: "wet" | "soiled";
  stoolColor?: string;
  stoolConsistency?: string;
  durationMin?: number;
  endedAt?: string;
  weightG?: number;
  lengthCm?: number;
  markerText?: string;
  noteText?: string;
  vomiting?: boolean;
  hiccups?: boolean;
  fever?: boolean;
  pain?: boolean;
  imageUrl?: string;
  imagePath?: string;
  pumpSide?: "left" | "right" | "both";
  pumpDurationMin?: number;
  fileUrl?: string;
  filePath?: string;
  fileName?: string;
  fileType?: string;
  vaccineIds?: string[];
  vaccineWeek?: number;
  isRoutineCheck?: boolean;
  taskId?: string;
  taskTitle?: string;
}

export interface LogEvent {
  id: string;
  babyId: string;
  type: LogType;
  occurredAt: string;
  payload: LogPayload;
  notes?: string;
  createdByUid: string;
}

// An in-progress sleep session, started on one device and visible to every caregiver with
// access to the baby until whoever's with the baby stops it (logging the completed sleep event).
export interface ActiveSleepSession {
  startedAt: string;
  startedByUid: string;
}

export interface DailyStats {
  date: string;
  feedTotalMl: number;
  feedCount: number;
  nursingCount?: number;
  sleepCount: number;
  diaperCountWet: number;
  diaperCountSoiled: number;
  weightLatestG?: number;
  alertsCount: number;
  pumpCount?: number;
  pumpTotalMl?: number;
}

export interface PercentileRange {
  p10: number;
  p20: number;
  p30: number;
  p40: number;
  p50: number;
  p95: number;
}

export interface DevelopmentalNorm {
  ageWeeks: number;
  feedingMinMl: number;
  feedingTargetMl: number;
  diaperMin: number;
  sleepNapsPerDay: number;
  milestones: string[];
  headsUp: string;
  eventName?: string;
  growthMinGPerWeek: number;
  growthMaxGPerWeek: number;
  boys?: PercentileRange;
  girls?: PercentileRange;
}

export interface MilestoneAchievement {
  id: string;
  babyId: string;
  ageWeeks: number;
  milestoneText: string;
  achievedAt: string;
  createdByUid: string;
}

export interface InsightSnapshot {
  status: Status;
  summaryText: string;
  headsUp: string;
  milestonesExpected: string[];
  redFlags: string[];
  deviation: {
    feedPct: number;
    diaperPct: number;
    growthGPerWeek: number | null;
  };
  pendingTasksCount?: number;
  overdueTasksCount?: number;
}

export interface Task {
  id: string;
  babyId: string;
  title: string;
  createdByUid: string;
  isRecurring: boolean;
  recurrenceType?: "interval" | "schedule";
  intervalConfig?: { value: number; unit: "hours" | "days" };
  scheduleConfig?: { daysOfWeek: string[] };
  nextDueAt: string;
  lastCompletedAt?: string;
  isActive: boolean;
}

export interface CopilotContext {
  currentTimeISO: string;
  ageWeeks: number;
  sex: "boy" | "girl" | null;
  loggingSinceISO: string;
  logs7days: string;
  completedMilestones: string[];
  normSummary?: string;
}

export interface AiResponse {
  weekRecap: string;
  weeklyDigest: string;
  developmentalLeap: string;
  personalizedBaseline: string;
  clusterFeeding: string;
  feedSleepWindows: string;
  hydrationSlope: string;
  growthForecast: string;
  sleepTrend: string;
  pumpingTrend: string;
  feedSleepCorrelation: string;
  caregiverLoad: string;
  sleepRegressionRisk: string;
  nightRisk: string;
  anomaly: string;
  stoolPattern: string;
  outlook: {
    feedingMl: number | null;
    diapers: number;
    naps: number;
    behavioralNote: string[];
  };
}

export interface CopilotInsight {
  id: string;
  label?: string;
  severity: "info" | "heads_up" | "watch";
  text: string;
}

export interface DevelopmentalLeapResult {
  approachingLeap: boolean;
  daysUntilNextLandmark: number | null;
  nextLandmarkWeek: number | null;
  headsUpPreview: string | null;
  eventName?: string;
  summary?: string;
}

export interface WeeklyOutlook {
  feedingMl: number | null;
  diapers: number | null;
  naps: number | null;
  behavioralNote: string[];
}
